// @flow
import {NavigationRoute, NavigationScreenProp} from '@react-navigation/compat';
import React, {Component} from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import runAfterInteractions from '../../utils/runAfterInteractions';
import {FlatList} from 'react-native-gesture-handler';
import {Text} from 'trpc-react-native-ui';
import {GradientPage, Icon} from '../../components/common';
import {monthNames} from '../../lib/date-utils';
import I18n from '../../lib/i18n';
import theme from '../../theme-config';
import WizzeeLog from '../../lib/wizzeeLog';

type Props = {
  fetchAvailableVouchers: Function,
  isRefreshingAvailableVouchers: boolean,
  navigation: NavigationScreenProp<NavigationRoute>,
  filteredAvailableVouchers: Object[],
  setCurrentVoucherDetails: Function,
};

const getStyles = (props) =>
  StyleSheet.create({
    list: {
      backgroundColor: 'white',
      marginTop: (props.comeFrom === 'chooseplan'|| 
                  props.comeFrom === 'confirmplan' ||
                  props.comeFrom === 'subscription' ||
                  props.comeFrom === 'confirmplanduringactivate') ? 40 : 0,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
    },
    emptyText: {
      textAlign: 'center',
      color: theme.secondaryColor,
      marginLeft: 20,
      marginRight: 20,
      fontSize: 18,
    },
    listTitle: {
      fontSize: 16,
      fontWeight: '500',
      marginVertical: 24,
      marginLeft: 20,
    },
    parentContainer: {
      backgroundColor: theme.backgroundColor,
      flex: 1,
    },
    itemParentContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingLeft: 20,
      paddingTop: 5,
      paddingRight: 20,
      paddingBottom: 5,
      marginLeft: 10,
      marginTop: 10,
      marginRight: 10,
      marginBottom: 0,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.secondaryColor,
    },
    itemLeftContainer: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      flex: 2,
    },
    itemRightContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      flex: 4,
      marginLeft: 20,
    },
    itemIcon: {
      alignSelf: 'center',
      flex: 1,
      marginBottom: 10,
      marginTop: 25,
    },
    itemDate: {
      alignSelf: 'center',
      flex: 1,
      textAlign: 'center',
    },
    itemTitle: {
      alignSelf: 'flex-start',
      flex: 2,
      color: theme.primaryColor,
      fontSize: 18,
      fontWeight: '600',
      marginTop: 5,
    },
    itemShortDescription: {
      alignSelf: 'flex-start',
      flex: 2,
      fontSize: 15,
      marginTop: 5,
      color: theme.secondaryColor,
    },
    itemMoreInfos: {
      alignSelf: 'flex-start',
      flex: 1,
      color: theme.primaryColor,
      fontSize: 13,
      marginTop: 20,
      textDecorationLine: 'underline',
    },
  });

class Vouchers extends Component<Props> {
  styles = getStyles(this.props);

  componentDidMount() {
    WizzeeLog.log('Vouchers-didmount, comeFrom: ', this.props.comeFrom);
    runAfterInteractions(() => {
      this.props.fetchAvailableVouchers();
    });
  }

  renderEmptyVouchersList = () => (
    <Text style={this.styles.emptyText}>
      {I18n.t('Dashboard.Vouchers.noVouchers')}
    </Text>
  );

  computeEnDate = item => {
    try {
      if (!item.endDateTime) {
        return I18n.t(
          'productOffering.PVOUCHER.productSpecification.characteristics.labelCode.voucherInfiniteEndDate',
        );
      }
      const voucherDate = new Date(item.endDateTime);
      const voucherUiDate = `${voucherDate.getDate()} ${
        monthNames[voucherDate.getMonth()]
      } ${voucherDate.getFullYear()}`;
      return I18n.t(
        'productOffering.PVOUCHER.productSpecification.characteristics.labelCode.voucherEndDate',
        {
          value: voucherUiDate,
        },
      );
    } catch (e) {
      return I18n.t('error.orderUnknownEndDateVoucher');
    }
  };

  renderItem = ({item}: Object) => {
    const goToVoucherDetails = () => {
      this.props.navigation.navigate('voucherDetails', {
        voucherDetails: item,
      });
    };

    const voucherDetails = item;

    return (
      <Pressable onPress={goToVoucherDetails}>
        <View style={this.styles.itemParentContainer}>
          <View style={this.styles.itemLeftContainer}>
            <Icon
              style={this.styles.itemIcon}
              name={I18n.t(
                `productOffering.PVOUCHER.productSpecification.characteristics.labelCode.${voucherDetails.labelCode}.picto`,
              )}
              size={60}
              color={'blue'}
            />
            <Text style={this.styles.itemDate}>
              {this.computeEnDate(voucherDetails)}
            </Text>
          </View>
          <View style={this.styles.itemRightContainer}>
            <Text style={this.styles.itemTitle}>
              {I18n.t(
                `productOffering.PVOUCHER.productSpecification.characteristics.labelCode.${voucherDetails.labelCode}.label`,
              )}
            </Text>
            <Text style={this.styles.itemShortDescription}>
              {I18n.t(
                `productOffering.PVOUCHER.productSpecification.characteristics.labelCode.${voucherDetails.labelCode}.shortDesc`,
              )}
            </Text>
            <Text style={this.styles.itemMoreInfos}>
              {I18n.t(
                `productOffering.PVOUCHER.productSpecification.characteristics.labelCode.${voucherDetails.labelCode}.knowMoreText`,
              )}
            </Text>
          </View>
        </View>
      </Pressable>
    );
  };

  keyExtractor = (item: Object, index: number) => item?.labelCode + '-' + index.toString();

  render() {
    const styles = this.styles;
    const {
      filteredAvailableVouchers,
      fetchAvailableVouchers,
      isRefreshingAvailableVouchers,
      comeFrom,
    } = this.props;
    const getContentContainerStyle =
      filteredAvailableVouchers.length <= 0 && styles.emptyContainer;
    const needsTopEdge = (
      comeFrom === 'subscription' ||
      comeFrom === 'chooseplan' ||
      comeFrom === 'confirmplanduringactivate' ||
      comeFrom === 'confirmplan'
    );

    const edgesByOs = () => {
      if (!needsTopEdge) {
        return ['left', 'right', 'bottom'];
      } else {
        return ['top', 'left', 'right', 'bottom'];
      }
    };
    
    return (
      <GradientPage iosEdges={edgesByOs()} androidEdges={edgesByOs()}>
        <FlatList
          style={styles.list}
          data={filteredAvailableVouchers}
          ListEmptyComponent={
            <Text style={styles.emptyText}>
              {I18n.t('Dashboard.ManageVouchers.noWizzeeGifts')}
            </Text>
          }
          refreshing={isRefreshingAvailableVouchers}
          onRefresh={fetchAvailableVouchers}
          renderItem={this.renderItem}
          keyExtractor={this.keyExtractor}
          contentContainerStyle={getContentContainerStyle}
          extraData={{now: Math.trunc(Date.now() / 15000)}} // date is necessary to rerender "time ago" (15sec delay)
        />
      </GradientPage>
    );
  }
}

export default Vouchers;
